import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ChevronLeft, ChevronRight, Loader2, Lock, Palette, Users } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { COLOR_META, CYCLE_ORDER } from "@/lib/colors";
import { renderLevel } from "@/components/SystemCell";
import { TeacherShell } from "@/components/TeacherShell";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const STD_CRITERIA = [
  { key: "respect_consignes", label: "Respect des consignes" },
  { key: "attention", label: "Attention" },
  { key: "comportement", label: "Comportement" },
];

function formatWeek(startISO, endISO) {
  const fmt = (d, o) => new Intl.DateTimeFormat("fr-FR", o).format(new Date(d));
  return `Semaine du ${fmt(startISO, { day: "numeric", month: "long" })} au ${fmt(endISO, { day: "numeric", month: "long", year: "numeric" })}`;
}

// Aperçu d'un niveau : pastille couleur (système standard) ou rendu du système personnalisé.
function LevelSwatch({ opt, isCustom, scale, size = "md" }) {
  if (isCustom) return renderLevel(opt.level, scale, size === "sm" ? "sm" : "md");
  const m = COLOR_META[opt.id];
  return <span className={`inline-block rounded-md ${size === "sm" ? "h-6 w-6" : "h-9 w-full"} ${m.cell}`} aria-hidden="true" />;
}

// Une ligne critère : palette + bouton « Appliquer à tous les élèves ».
function CriterionRow({ crit, options, isCustom, scale, selected, onPick, onApply, disabled, count }) {
  const sel = options.find((o) => o.id === selected);
  return (
    <section className="rounded-2xl border border-slate-100 bg-white p-4 shadow-[0_4px_20px_rgb(0,0,0,0.03)] dark:border-white/10 dark:bg-white/[0.03] sm:p-5" data-testid={`bulk-criterion-${crit.key}`}>
      <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">{crit.label}</h2>
      <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">Choisissez un niveau puis appliquez-le à toute la classe pour ce critère uniquement.</p>
      <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6" role="radiogroup" aria-label={`Niveau pour ${crit.label}`}>
        {options.map((opt) => {
          const active = opt.id === selected;
          return (
            <button
              key={opt.id}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => onPick(crit.key, opt.id)}
              className={`flex min-h-[48px] items-center gap-2.5 rounded-xl border px-2.5 py-2 text-left text-sm transition-[transform,border-color,box-shadow] duration-150 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 ${
                active ? "border-slate-900 bg-slate-50 shadow-sm dark:border-white dark:bg-white/10" : "border-slate-200 hover:border-slate-300 dark:border-white/10 dark:hover:border-white/20"
              }`}
              data-testid={`bulk-option-${crit.key}-${opt.id}`}
              data-selected={active}
            >
              <span className="shrink-0"><LevelSwatch opt={opt} isCustom={isCustom} scale={scale} size="sm" /></span>
              <span className="leading-tight text-slate-700 dark:text-slate-200">{opt.label}</span>
            </button>
          );
        })}
      </div>
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-slate-500 dark:text-slate-400" data-testid={`bulk-selection-${crit.key}`}>
          {sel ? <>Sélection : <strong className="text-slate-800 dark:text-slate-100">{sel.label}</strong></> : "Aucun niveau sélectionné"}
        </p>
        <Button
          className="rounded-xl"
          disabled={!sel || disabled}
          onClick={() => onApply(crit, sel)}
          data-testid={`bulk-apply-${crit.key}`}
        >
          <Users size={16} className="mr-1.5" /> Appliquer à tous les élèves{count ? ` (${count})` : ""}
        </Button>
      </div>
    </section>
  );
}

export default function BulkColors() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const offset = Number(params.get("offset") || 0);
  const [meta, setMeta] = useState(null);
  const [system, setSystem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState({});
  const [confirm, setConfirm] = useState(null); // { crit, opt }
  const [applying, setApplying] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/classes/${id}/week`, { params: { offset } });
      setMeta({ class: data.class, start: data.week_start, end: data.week_end, count: data.rows.length });
      setSystem(data.eval_system || null);
    } catch (e) {
      toast.error(e.response?.status === 404 ? "Classe introuvable." : "Chargement impossible.");
    } finally {
      setLoading(false);
    }
  }, [id, offset]);

  useEffect(() => { load(); }, [load]);

  const isCustom = system?.type === "custom";
  const criteria = isCustom ? system.criteria.map((c) => ({ key: c.id, label: c.label })) : STD_CRITERIA;
  const options = isCustom
    ? system.levels.map((l) => ({ id: l.id, label: l.label, level: l }))
    : CYCLE_ORDER.map((c) => ({ id: c, label: COLOR_META[c].meaning }));
  const readOnly = !!meta?.class?.archived;

  const apply = async () => {
    if (!confirm) return;
    setApplying(true);
    try {
      const { data } = await api.post(`/classes/${id}/passports/bulk-color`, { offset, criterion: confirm.crit.key, value: confirm.opt.id });
      toast.success(`« ${confirm.opt.label} » appliqué à ${data.updated} élève${data.updated > 1 ? "s" : ""} pour « ${confirm.crit.label} ».`);
      setConfirm(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de l'application collective.");
    } finally {
      setApplying(false);
    }
  };

  const setOffset = (o) => setParams(o ? { offset: String(o) } : {});

  return (
    <TeacherShell active="dashboard">
      <div className="mx-auto max-w-5xl px-4 py-6 sm:px-6">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <button
              onClick={() => navigate(`/enseignant/classe/${id}`)}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-transform hover:scale-95 dark:border-white/10 dark:bg-white/5 dark:text-slate-300"
              data-testid="back-to-table"
              aria-label="Retour au Passeport"
            >
              <ArrowLeft size={18} />
            </button>
            <div className="min-w-0">
              <h1 className="flex items-center gap-2 text-xl font-semibold tracking-tight text-slate-900 dark:text-slate-100 sm:text-2xl">
                <Palette size={22} className="shrink-0 text-slate-400" /> Couleurs collectives
              </h1>
              <p className="truncate text-xs text-slate-500 dark:text-slate-400" data-testid="bulk-subtitle">
                {meta?.class?.name}{meta ? ` · ${formatWeek(meta.start, meta.end)} · ${meta.count} élève${meta.count > 1 ? "s" : ""}` : ""}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => setOffset(offset - 1)} className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 dark:border-white/10 dark:bg-white/5 dark:text-slate-300" data-testid="prev-week" aria-label="Semaine précédente"><ChevronLeft size={18} /></button>
            {offset !== 0 && <button onClick={() => setOffset(0)} className="rounded-lg px-2 py-1.5 text-xs font-medium text-slate-500 hover:text-slate-900" data-testid="current-week">Aujourd'hui</button>}
            <button onClick={() => setOffset(offset + 1)} className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 dark:border-white/10 dark:bg-white/5 dark:text-slate-300" data-testid="next-week" aria-label="Semaine suivante"><ChevronRight size={18} /></button>
          </div>
        </div>

        {readOnly && (
          <div className="mb-4 flex items-start gap-2 rounded-xl bg-amber-50 p-4 text-sm text-amber-700 dark:bg-amber-500/10 dark:text-amber-400" data-testid="archived-readonly-banner">
            <Lock size={16} className="mt-0.5 shrink-0" /> Classe archivée : aucune modification possible.
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-24" data-testid="bulk-loading"><Loader2 className="animate-spin text-slate-400" size={28} /></div>
        ) : (
          <div className="flex flex-col gap-4" data-testid="bulk-criteria-list">
            {criteria.map((crit) => (
              <CriterionRow
                key={crit.key} crit={crit} options={options} isCustom={isCustom} scale={system?.scale_type}
                selected={selected[crit.key]} onPick={(k, v) => setSelected((s) => ({ ...s, [k]: v }))}
                onApply={(c, opt) => setConfirm({ crit: c, opt })} disabled={readOnly || !meta?.count} count={meta?.count}
              />
            ))}
          </div>
        )}
      </div>

      <AlertDialog open={!!confirm} onOpenChange={(v) => { if (!v && !applying) setConfirm(null); }}>
        <AlertDialogContent className="max-w-[calc(100%-2rem)] rounded-2xl sm:max-w-lg" data-testid="bulk-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Appliquer à tous les élèves ?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm text-slate-600 dark:text-slate-300">
                <p>
                  Le critère <strong className="text-slate-900 dark:text-slate-100" data-testid="bulk-confirm-criterion">{confirm?.crit.label}</strong> sera
                  défini pour <strong>{meta?.count} élève{meta?.count > 1 ? "s" : ""}</strong> ({meta && formatWeek(meta.start, meta.end).toLowerCase()}) sur :
                </p>
                {confirm && (
                  <div className="flex items-center gap-3 rounded-xl border border-slate-200 p-3 dark:border-white/10" data-testid="bulk-confirm-level">
                    <span className="shrink-0"><LevelSwatch opt={confirm.opt} isCustom={isCustom} scale={system?.scale_type} size="sm" /></span>
                    <span className="font-medium text-slate-900 dark:text-slate-100">{confirm.opt.label}</span>
                  </div>
                )}
                <p className="text-xs">Les autres critères, les notions à revoir, les commentaires et les signatures ne sont pas modifiés.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl" disabled={applying} data-testid="bulk-confirm-cancel">Annuler</AlertDialogCancel>
            <AlertDialogAction className="rounded-xl" onClick={(e) => { e.preventDefault(); apply(); }} disabled={applying} data-testid="bulk-confirm-apply">
              {applying ? <Loader2 size={16} className="animate-spin" /> : "Confirmer"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </TeacherShell>
  );
}
